// providers
import { NotificationService } from './providers';
import NotificationsCenterStore from './store';
// typings
import {
  INotification,
  INotificationCenter,
  INotificationProvider,
  INotificationVariant,
} from './typings';

class NotificationCenterService
extends NotificationsCenterStore
implements INotificationCenter {
  private readonly notificationProvider: INotificationProvider;

  constructor() {
    super();
    this.notificationProvider = NotificationService;
  }

  async loadNotifications() {
    // Fetch both notifications and expiring notifications concurrently
    const results = await Promise.allSettled([
      this.notificationProvider.getNotifications(),
      this.notificationProvider.getExpiringNotifications(),
    ]);
    const notifications =
      results[0]?.status === 'fulfilled' && results[0].value
        ? results[0].value
        : [];

    const expiringNotifications =
      results[1]?.status === 'fulfilled' && results[1].value
        ? results[1].value
        : [];

    this.processNotifications([...notifications, ...expiringNotifications]);
  }

  delete = (notification: INotification) => {
    if (notification) {
      const isHybrid = notification.id.includes('-hybrid');
      if (isHybrid) {
        const remoteId = notification.id.split('-hybrid')[0];
        this.deleteNotification(
          { ...notification, id: `${remoteId}-hybrid-bar` },
          'bar',
        );
        this.deleteNotification(
          { ...notification, id: `${remoteId}-hybrid-modal` },
          'modal',
        );
      } else {
        this.deleteNotification(notification, notification.variant);
      }
    }
  };

  markAsRead = (notification: INotification) => {
    if (notification) {
      this.notificationProvider.markAsRead(notification);
    }
  };

  push = (notification: INotification, variant: INotificationVariant) => {
    let notifications: INotification[] = [];
    let notificationToPush = notification;
    switch (variant) {
      case 'bar':
        // check if id already exists
        notifications = this.barNotifications;
        notificationToPush = { ...notification, variant: 'bar' };
        break;
      case 'modal':
        // check if id already exists
        notifications = this.modalNotifications;
        notificationToPush = { ...notification, variant: 'modal' };
        break;
      default:
        break;
    }

    const notificationIndex = notifications.findIndex(
      (item) => item.id === notificationToPush.id,
    );

    if (notificationIndex > -1) {
      // update
      this.updateNotification(notificationToPush, variant);
    } else {
      // add
      this.addNotification(notificationToPush, variant);
    }
  };

  private processNotifications(notifications: INotification[]) {
    if (notifications) {
      const barNotifications = notifications.filter(
        (item) => item.variant === 'bar',
      );
      const modalNotifications = notifications.filter(
        (item) => item.variant === 'modal',
      );

      barNotifications.forEach((item) => {
        this.push(item, 'bar');
      });
      modalNotifications.forEach((item) => {
        this.push(item, 'modal');
      });
    }
  }
}

export default new NotificationCenterService();
